import {
  Component,
  computed,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { FwDialogService } from '@flywheel-io/vision';
import { Observable, of,Subscription } from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { ContainerAttachmentsService, DownloadOptions, FileIdentifier } from '../../containers/attachments/container-attachments.service';
import { DialogService } from '../../dialogs';
import { DeleteDialogComponent, DeleteDialogResult } from '../../dialogs/delete-dialog/delete-dialog.component';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { copyTextToClipboard } from '../../shared/browser.utils';
import { getContainerProjectId, getRoleMatrix } from '../../shared/container.utils';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { FileAction } from '../../shared/enums/file.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { ReaderTaskContainerType } from '../../shared/enums/task.enum';
import { getFileClassification } from '../../shared/file.utils';
import { Container } from '../../shared/models/container.model';
import { CVATSyncState, File } from '../../shared/models/file.model';
import { ViewerApp } from '../../shared/models/site-settings.model';
import { firstValueFrom } from '../../shared/observable.utils';
import {
  CreateReaderTaskDialogComponent,
} from '../../task-manager/create-reader-task-dialog/create-reader-task-dialog.component';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { ViewerParameters } from '../../viewers/frame/viewer-frame.model';
import { ViewerService } from '../../viewers/viewer.service';
import { hasBaseViewer } from '../../viewers/viewer.utils';
import { FileInfoDialog, FileInfoDialogResult } from '../file-info-dialog/file-info-dialog.component';

type ActionMap = Partial<Record<FileAction, boolean>>;
import { FileType, getViewerType } from '../../viewers/types';
import { AnnotationQueryService } from '../../sessions/annotation-list/annotation.service';
import { injectQuery } from '@tanstack/angular-query-experimental';

@Component({
  selector: 'file-actions',
  templateUrl: './file-actions.component.html',
  styles: [`
    :host {
      display: flex;
      gap: 4px;

      &.force-visible {
        opacity: 1 !important;
      }

      ::ng-deep .mat-mdc-icon-button.mat-mdc-button-base {
        padding: 0 !important;
        --mdc-icon-button-state-layer-size: 24px !important;
      }
    }
  `],
})
export class FileActionsComponent implements OnChanges, OnDestroy {
  @Input() file: File
  @Input({ required: true }) container: Container
  @Input() hoverFeatures: FileAction[] = []
  @Input() kabobFeatures: FileAction[] = [FileAction.Download, FileAction.View]
  @Input() viewParams?: ViewerParameters | ((container: Container, file?: File) => ViewerParameters)

  @Output() afterChange = new EventEmitter<File>()
  @Output() afterDelete = new EventEmitter<File>()

  // for forcing the icons to stay visible when the user is hovering the annotation panel
  @HostBinding('class.force-visible') forceVisible = false;

  public viewable$ = of(false)
  public viewerApps$: Observable<ViewerApp[]>
  public modalityDisplay: string = null
  public features: { hover: ActionMap, kabob: ActionMap } = {
    hover: {
      [FileAction.Cvat]: false,
      [FileAction.View]: false,
      [FileAction.Information]: false,
      [FileAction.Download]: false,
    },
    kabob: {
      [FileAction.Information]: false,
      [FileAction.Download]: false,
      [FileAction.Delete]: false,
      [FileAction.VersionHistory]: false,
      [FileAction.CopyFilename]: false,
      [FileAction.CopyModality]: false,
      [FileAction.View]: false,
      [FileAction.Cvat]: false,
      [FileAction.Tasks]: false,
    },
  }

  protected showingFileNameCopy = true
  protected showingModalityCopy = true

  // this is a hacky way of getting a signal of file changes
  // switching to a signal input for this.file would obviate the need for this
  private fileIdSignal = signal<string>('');
  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    messages: Subscription.EMPTY,
    profile: Subscription.EMPTY,
  }

  constructor(
    private configService: ConfigService,
    private annotationQueries: AnnotationQueryService,
    private containerAttachments: ContainerAttachmentsService,
    private dialog: DialogService,
    private fwDialog: FwDialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private translate: TranslationService,
    private viewerService: ViewerService,
  ) {
    this.subscriptions.profile = this.profileService.getProfile().pipe(
      filter(profile => Boolean(profile)),
    ).subscribe(() => {
      this.updateFeatures();
    });
    this.subscriptions.messages = this.translate.getBundle('fileActions').subscribe(messages => {
      this.messages = messages;
    });
    this.fileIdSignal.set(this.file?.file_id);
  }

  viewableInOhif = signal(false);
  annotationQuery = injectQuery(() => this.annotationQueries.byFile(this.fileIdSignal))

  annotationCount = computed(() => {
    return this.annotationQuery.data()?.length || 0;
  })

  private get fileIdentifier(): FileIdentifier {
    return {
      fileId: this.file.file_id,
      version: this.file.version,
      // for search results (until FLYW-11952)
      containerId: this.container._id,
      fileName: this.file.name,
    };
  }

  get showKabobMenu(): boolean {
    return Object.values(this.features.kabob).some(value => value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.file && this.file) {

      this.fileIdSignal.set(this.file.file_id);

      let projectId: string;
      if (this.container) {
        projectId = getContainerProjectId(this.container);
      }
      this.viewerApps$ = this.viewerService.getAssociatedApps({ file: this.file, projectId }).pipe(
        map(apps => apps.filter(app => app.type !== 'form'))
      );
      this.viewable$ = this.viewerApps$.pipe(
        map(apps => {
          const type = getViewerType(this.file);
          this.viewableInOhif.set([FileType.dicom, FileType.nifti].includes(type));
          if (this.viewableInOhif()) {
            // require an installed viewer AND an enabled viewer
            return apps.length > 0 && this.viewerService.isAnyOhifViewerEnabled();
          }
          else {
            return apps.length > 0 || hasBaseViewer(this.file);
          }
        }),
      );
      this.modalityDisplay = getFileClassification(this.file);
    }
    if (changes.container && this.container) {
      this.updateFeatures();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  copyToClipboard(input: string): void {
    copyTextToClipboard(input);
    setTimeout(() => {
      this.showingFileNameCopy = this.showingModalityCopy = true;
    }, 1000);
  }

  protected createReaderTask(): void {
    this.fwDialog.openDialog(CreateReaderTaskDialogComponent, {
      closeOnDestroy: true,
      disableClose: true,
      data: {
        projectId: getContainerProjectId(this.container),
        containerType: ReaderTaskContainerType.File,
        containerId: this.file.file_id,
      },
    }).closed.pipe(take(1)).subscribe();
  }

  delete(): void {
    this.dialog.open(DeleteDialogComponent, {
      data: { content: this.translate.interpolate(this.messages.get('labels.deleteConfirm'), this.file.name) },
    }).afterClosed().pipe(
      filter((result: DeleteDialogResult) => result.delete),
      switchMap(result => {
        const deleteParams = result.deleteReason ? { delete_reason: result.deleteReason } : {};
        return this.containerAttachments.delete(this.fileIdentifier, { ...deleteParams, force: true })
      }),
    ).subscribe({
      next: () => {
        this.notifications.success('container.file.delete.success');
        this.afterDelete.next(this.file);
      },
      error: error => {
        if (error.error?.reason === 'analysis_conflict') {
          this.notifications.error('container.file.delete.analysis_conflict');
          return;
        }
        this.logger.error(error);
        this.notifications.error('container.file.delete.error');
      },
    });
  }

  download(): void {
    this.containerAttachments.download(this.fileIdentifier as DownloadOptions);
  }

  information(): void {
    // Get the FULL file from the API, the input is probably a stub from a container overview
    this.containerAttachments.metadata(this.fileIdentifier).subscribe(file => {
      // conversely, the file passed in may have data modified on it because the session controller is a :dumpster_fire:
      const mergedFile = Object.assign({}, this.file, file);
      this.dialog.open(FileInfoDialog, {
        data: { file: mergedFile, container: this.container },
      }).afterClosed().subscribe((result: FileInfoDialogResult) => {
        if (result?.dirty) {
          this.afterChange.emit(this.file);
        }
      });
    });
  }

  versionHistory(): void {
    this.containerAttachments.versionHistory(this.file).then(modified => {
      if (modified) {
        this.afterChange.emit(this.file);
      }
    });
  }

  async viewInApp(app?: ViewerApp): Promise<void> {
    if (!this.container.parents) {
      this.container = await this.coreApi.containers.get<Container>(this.container._id).toPromise();
    }
    const result = await firstValueFrom(this.viewerService.view({
      app: app,
      container: this.container,
      file: this.file,
      params: typeof this.viewParams === 'function' ? this.viewParams(this.container, this.file) : this.viewParams || {},
    }));
    if (result?.dirty) {
      this.afterChange.emit(this.file);
    }
  }

  launchInCVAT(): void {
    this.coreApi.cvat.fileAccess.post(this.file.file_id).subscribe(cvatURL => {
      window.open(cvatURL, '_blank').focus();
    });
  }

  private updateFeatures(): void {
    if (!this.container) {
      return;
    }

    const projectId = getContainerProjectId(this.container);
    if (projectId) {
      this.projectsService.getProjectPermissions(projectId).subscribe(projectPermissions => {
        this.features = {
          hover: {
            [FileAction.Cvat]: this.configService.isFeatureEnabled('cvat') && this.file.cvat?.sync_state === CVATSyncState.Synced,
            [FileAction.View]: this.hoverFeatures.includes(FileAction.View) && projectPermissions.includes(PermissionAction.FilesViewContents),
            [FileAction.Information]: this.hoverFeatures.includes(FileAction.Information) && projectPermissions.includes(PermissionAction.FilesViewMetadata),
            [FileAction.Download]: this.hoverFeatures.includes(FileAction.Download) && projectPermissions.includes(PermissionAction.FilesDownload),
          },
          kabob: {
            [FileAction.Information]: this.kabobFeatures.includes(FileAction.Information) && projectPermissions.includes(PermissionAction.FilesViewMetadata),
            [FileAction.Download]: this.kabobFeatures.includes(FileAction.Download) && projectPermissions.includes(PermissionAction.FilesDownload),
            [FileAction.Delete]: this.kabobFeatures.includes(FileAction.Delete) && projectPermissions.includes(this.file.origin?.type === 'device' ? PermissionAction.FilesDeleteDeviceData : PermissionAction.FilesDeleteNonDeviceData),
            [FileAction.VersionHistory]: this.file.version && this.kabobFeatures.includes(FileAction.VersionHistory) && projectPermissions.includes(PermissionAction.FilesViewMetadata),
            [FileAction.CopyFilename]: this.kabobFeatures.includes(FileAction.CopyFilename) && projectPermissions.includes(PermissionAction.FilesViewContents),
            [FileAction.CopyModality]: this.kabobFeatures.includes(FileAction.CopyModality) && projectPermissions.includes(PermissionAction.FilesViewContents),
            [FileAction.View]: this.kabobFeatures.includes(FileAction.View) && projectPermissions.includes(PermissionAction.FilesViewContents),
            [FileAction.Cvat]: this.configService.isFeatureEnabled('cvat') && this.file.cvat?.sync_state === CVATSyncState.Synced,
            [FileAction.Tasks]: this.kabobFeatures.includes(FileAction.Tasks) && this.configService.isFeatureEnabled('tasks_refactor') && projectPermissions.includes(PermissionAction.TasksModify),
          },
        };
      });
    } else {
      // Fall back to roleMatrix if the container doesn't have roles
      this.profileService.getProfile().pipe(first()).subscribe(profile => {
        const roleMatrix = getRoleMatrix(this.container, profile);
        this.features = {
          hover: {
            [FileAction.Cvat]: this.configService.isFeatureEnabled('cvat') && this.file.cvat?.sync_state === CVATSyncState.Synced,
            [FileAction.View]: this.hoverFeatures.includes(FileAction.View) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.Information]: this.hoverFeatures.includes(FileAction.Information) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.Download]: this.hoverFeatures.includes(FileAction.Download) && roleMatrix[ContainerAccess.ReadOnly],
          },
          kabob: {
            [FileAction.Information]: this.kabobFeatures.includes(FileAction.Information) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.Download]: this.kabobFeatures.includes(FileAction.Download) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.Delete]: this.kabobFeatures.includes(FileAction.Delete) && roleMatrix[ContainerAccess.ReadWrite],
            [FileAction.VersionHistory]: this.file.version && this.kabobFeatures.includes(FileAction.VersionHistory) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.CopyFilename]: this.kabobFeatures.includes(FileAction.CopyFilename) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.CopyModality]: this.kabobFeatures.includes(FileAction.CopyModality) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.View]: this.kabobFeatures.includes(FileAction.View) && roleMatrix[ContainerAccess.ReadOnly],
            [FileAction.Cvat]: this.configService.isFeatureEnabled('cvat') && this.file.cvat?.sync_state === CVATSyncState.Synced,
          },
        };
      });
    }
  }
}
